import React, { useState, useRef, useEffect } from 'react';
import ModelViewer from '../atoms/ModelViewer';
import './FloatingCharacterModel.css';

const FloatingCharacterModel = ({ 
  character, 
  modelSrc, 
  size = 200,
  position = { top: '50%', left: '50%' },
  delay = 0,
  className = "" 
}) => {
  const [velocity, setVelocity] = useState({ x: 0, y: 0 });
  const [pos, setPos] = useState({
    x: parseFloat(position.left) || 50,
    y: parseFloat(position.top) || 50
  });
  const modelRef = useRef(null);
  const animationRef = useRef(null);
  const isAnimating = useRef(true); // Always animate to keep models floating
  const bounceSound = useRef(null);
  const lastBounceTime = useRef(0);
  const floatTimer = useRef(null);
  const floatOffset = useRef({
    x: Math.random() * 2 - 1,
    y: Math.random() * 2 - 1
  });

  // Initialize bounce sound and start floating animation immediately
  useEffect(() => {
    // Create an audio context
    bounceSound.current = new Audio();
    bounceSound.current.src = 'data:audio/mp3;base64,//uQxAAAEvGLIVT0AAuBtax3P2QCYAMA4OCgYHEe5LRuikbGQxt6P/3cYxjkMcZ/GvH/8deGQxjkOYx9dflkT1CEIQhDGMY5L58QQh3sYx96HIMaj0v/5znf/znP/8IHAQ4CAgQO9CAagAAAAAP/Qg9bJYkknOADVTg9w4YYhweu6ECyB/yeR7Pc+1oEDRLskyUcLMvyoDQPGFMCrnjUdpeMDXHDQoAk38/+T/qSM3/1P+7/1f/CgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA==';
    bounceSound.current.volume = 0.3;

    // Start smooth floating animation right away
    animationRef.current = requestAnimationFrame(updatePosition);
    
    // Change float direction periodically
    updateFloatDirection();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (floatTimer.current) {
        clearTimeout(floatTimer.current);
      }
      bounceSound.current = null;
    };
  }, []);

  // Change the float direction periodically to create natural movement
  const updateFloatDirection = () => {
    floatOffset.current = {
      x: Math.random() * 2 - 1,
      y: Math.random() * 2 - 1
    };
    
    // Change direction every 3-7 seconds
    const nextChangeTime = 3000 + Math.random() * 4000;
    floatTimer.current = setTimeout(updateFloatDirection, nextChangeTime);
  };

  // Convert percentage to viewport units
  const percentToVW = (percent) => window.innerWidth * (percent / 100);
  const percentToVH = (percent) => window.innerHeight * (percent / 100);
  
  // Convert viewport units to percentage
  const vwToPercent = (vw) => (vw / window.innerWidth) * 100;
  const vhToPercent = (vh) => (vh / window.innerHeight) * 100;

  // Play bounce sound with debounce
  const playBounceSound = () => {
    const now = Date.now();
    if (now - lastBounceTime.current > 150) { // Debounce 150ms
      if (bounceSound.current) {
        // Reset and play for quick reactions
        bounceSound.current.currentTime = 0;
        bounceSound.current.play().catch(e => console.log("Sound play error:", e));
        lastBounceTime.current = now;
      }
    }
  };

  const handleDoubleClick = (e) => {
    if (!modelRef.current) return;
    
    e.stopPropagation(); // Prevent parent clicks
    
    // Get model center position
    const modelRect = modelRef.current.getBoundingClientRect();
    const modelCenterX = modelRect.left + modelRect.width / 2;
    const modelCenterY = modelRect.top + modelRect.height / 2;
    
    // Calculate click position relative to model center
    const clickX = e.clientX - modelCenterX;
    const clickY = e.clientY - modelCenterY;
    
    // Calculate direction vector (opposite of click direction)
    const magnitude = Math.sqrt(clickX * clickX + clickY * clickY);
    const directionX = -clickX / magnitude; // Negative for opposite direction
    const directionY = -clickY / magnitude; // Negative for opposite direction
    
    // Apply force (larger number = stronger kick)
    const kickForce = 20; // Increased force for more dramatic effect
    setVelocity({
      x: directionX * kickForce,
      y: directionY * kickForce
    });
    
    // Add "kick" class for visual feedback
    modelRef.current.classList.add('kicked');
    setTimeout(() => {
      if (modelRef.current) {
        modelRef.current.classList.remove('kicked');
      }
    }, 300);
    
    // Play sound
    playBounceSound();
  };

  const updatePosition = () => {
    // Apply physics
    setPos(prevPos => {
      // Convert percentage to actual viewport pixels for calculations
      const posXpx = percentToVW(prevPos.x);
      const posYpx = percentToVH(prevPos.y);
      
      // Get velocity magnitude to determine if we're in physics mode or floating mode
      const velocityMagnitude = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y);
      
      let newXpx, newYpx;
      
      if (velocityMagnitude > 0.5) {
        // Physics mode - after a kick
        // Apply velocity to position
        newXpx = posXpx + velocity.x;
        newYpx = posYpx + velocity.y;
        
        // Apply minimal friction
        setVelocity(prev => ({
          x: prev.x * 0.99,
          y: prev.y * 0.99
        }));
      } else {
        // Gentle floating mode - default state
        // If velocity is very low, switch to smooth floating animation
        const floatSpeed = 0.3; // Slow gentle movement
        newXpx = posXpx + floatOffset.current.x * floatSpeed;
        newYpx = posYpx + floatOffset.current.y * floatSpeed;
        
        // Clear any remaining velocity
        if (velocityMagnitude > 0) {
          setVelocity({ x: 0, y: 0 });
        }
      }
      
      // Check boundaries (edge collision)
      const halfSize = size / 2;
      const maxX = window.innerWidth - halfSize;
      const maxY = window.innerHeight - halfSize;
      
      let didBounce = false;
      let newVelocity = { ...velocity };
      
      // Handle X boundary
      if (newXpx < halfSize) {
        // Calculate the reflection
        const overshot = halfSize - newXpx;
        newXpx = halfSize + overshot;
        newVelocity.x = -newVelocity.x;
        didBounce = true;
      } else if (newXpx > maxX) {
        const overshot = newXpx - maxX;
        newXpx = maxX - overshot;
        newVelocity.x = -newVelocity.x;
        didBounce = true;
      }
      
      // Handle Y boundary
      if (newYpx < halfSize) {
        const overshot = halfSize - newYpx;
        newYpx = halfSize + overshot;
        newVelocity.y = -newVelocity.y;
        didBounce = true;
      } else if (newYpx > maxY) {
        const overshot = newYpx - maxY;
        newYpx = maxY - overshot;
        newVelocity.y = -newVelocity.y;
        didBounce = true;
      }
      
      // Update velocity if we bounced
      if (didBounce) {
        setVelocity(newVelocity);
        
        // Play bounce sound
        playBounceSound();
        
        // Add a visual effect for the bounce
        if (modelRef.current) {
          modelRef.current.classList.add('bounced');
          setTimeout(() => {
            if (modelRef.current) {
              modelRef.current.classList.remove('bounced');
            }
          }, 200);
        }
      }
      
      // Prevent getting stuck in the corners by ensuring we're moving away
      const safetyMargin = size / 4;
      
      // Check if we're in a corner and moving in the wrong direction
      if (newXpx < halfSize + safetyMargin && newYpx < halfSize + safetyMargin) {
        // Top-left corner
        if (newVelocity.x <= 0 && newVelocity.y <= 0) {
          setVelocity({
            x: Math.random() * 3 + 1,
            y: Math.random() * 3 + 1
          });
        }
      } else if (newXpx > maxX - safetyMargin && newYpx < halfSize + safetyMargin) {
        // Top-right corner
        if (newVelocity.x >= 0 && newVelocity.y <= 0) {
          setVelocity({
            x: -(Math.random() * 3 + 1),
            y: Math.random() * 3 + 1
          });
        }
      } else if (newXpx < halfSize + safetyMargin && newYpx > maxY - safetyMargin) {
        // Bottom-left corner
        if (newVelocity.x <= 0 && newVelocity.y >= 0) {
          setVelocity({
            x: Math.random() * 3 + 1,
            y: -(Math.random() * 3 + 1)
          });
        }
      } else if (newXpx > maxX - safetyMargin && newYpx > maxY - safetyMargin) {
        // Bottom-right corner
        if (newVelocity.x >= 0 && newVelocity.y >= 0) {
          setVelocity({
            x: -(Math.random() * 3 + 1),
            y: -(Math.random() * 3 + 1)
          });
        }
      }
      
      // Convert back to percentage
      return {
        x: vwToPercent(newXpx),
        y: vhToPercent(newYpx)
      };
    });
    
    // Always continue the animation loop
    animationRef.current = requestAnimationFrame(updatePosition);
  };

  const style = {
    top: `${pos.y}%`,
    left: `${pos.x}%`,
    animationDelay: `${delay}s`
  };

  return (
    <div 
      ref={modelRef}
      onDoubleClick={handleDoubleClick}
      className={`floating-character-model ${className}`}
      style={{ 
        position: 'absolute',
        top: `${pos.y}%`, 
        left: `${pos.x}%`,
        transition: `top ${delay}s ease-out, left ${delay}s ease-out`
      }}
    >
      <ModelViewer
        src={modelSrc}
        alt={`3D model of Chinese character ${character}`}
        width={`${size}px`}
        height={`${size}px`}
        autoRotate={true}
        cameraControls={false}
        className="floating-model"
      />
    </div>
  );
};

export default FloatingCharacterModel;